import { useState, useEffect } from 'react';

const EntryFoodTunnel = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('entry-food-tunnel');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % tunnelImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const tunnelImages = [
    {
      url: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      title: 'Welcome Pathway',
      description: 'Natural entrance surrounded by growing plants'
    },
    {
      url: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      title: 'Green Tunnel',
      description: 'Immersive walkway through fresh produce'
    },
    {
      url: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      title: 'Farm Gateway',
      description: 'Your journey into sustainable farming begins'
    }
  ];

  const features = [
    {
      icon: 'ri-plant-line',
      title: 'Living Walls',
      description: 'Vertical gardens creating a natural tunnel experience'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Fresh Air',
      description: 'Oxygen-rich environment from surrounding plants'
    },
    {
      icon: 'ri-camera-line',
      title: 'Photo Spots',
      description: 'Instagram-worthy moments at every turn'
    },
    {
      icon: 'ri-heart-line',
      title: 'Sensory Experience',
      description: 'Touch, smell, and see nature up close'
    }
  ];

  return (
    <section id="entry-food-tunnel" className="py-20 bg-gradient-to-b from-green-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Entry Food Tunnel Experience
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Step into our unique Entry Food Tunnel where your farm journey begins with an immersive walkway through nature's bounty
          </p>
        </div>

        {/* Main Tunnel Display */}
        <div className={`mb-16 transform transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="relative h-96 md:h-[500px] rounded-3xl overflow-hidden shadow-2xl">
            {tunnelImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === activeImage ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img 
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-8 left-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">{image.title}</h3>
                  <p className="text-lg text-gray-200">{image.description}</p>
                </div>
              </div>
            ))}
            
            {/* Navigation Dots */}
            <div className="absolute bottom-4 right-8 flex space-x-2">
              {tunnelImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImage(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                    index === activeImage ? 'bg-white' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16 transform transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center"
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center text-green-600">
                <i className={`${feature.icon} text-2xl`}></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Journey Steps */}
        <div className={`bg-white rounded-3xl p-8 md:p-12 shadow-xl transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">Your Tunnel Journey</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                1
              </div>
              <h4 className="text-xl font-semibold text-gray-800 mb-2">Enter</h4>
              <p className="text-gray-600">Step into the natural archway surrounded by climbing plants and fresh herbs</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                2
              </div>
              <h4 className="text-xl font-semibold text-gray-800 mb-2">Experience</h4>
              <p className="text-gray-600">Walk through the living tunnel, touching and smelling fresh produce</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                3
              </div>
              <h4 className="text-xl font-semibold text-gray-800 mb-2">Emerge</h4>
              <p className="text-gray-600">Exit into the main farm area, ready for your agricultural adventure</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EntryFoodTunnel;